/* oji-v4-ugc.js — Oji V4 Comunidad (UGC video reel)
   Vanilla JS reimplementation of the v3 UGC reel behavior
   (assets/oji-pdp-ugc.js), namespaced for the v4 section:
   - Viewport-aware playback: play (muted) at >=60% visible, pause on exit
   - prefers-reduced-motion: never autoplay
   - Real play/pause + mute/unmute buttons with aria-pressed / aria-label sync
   - Desktop prev/next arrows scrolling the snap reel, disabled-state managed
   No jQuery, no GSAP dependency. */
(function () {
  'use strict';

  if (window.__ojiV4UgcInit) return;
  window.__ojiV4UgcInit = true;

  var VISIBLE_RATIO = 0.6;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function playCard(card) {
    var video = card.querySelector('.ugc__video');
    if (!video) return;
    var promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {}); // autoplay rejection is non-fatal
    }
  }

  function pauseCard(card) {
    var video = card.querySelector('.ugc__video');
    if (!video) return;
    video.pause();
  }

  function syncPlayUI(card, video, playBtn) {
    var playing = !video.paused && !video.ended;
    card.classList.toggle('is-playing', playing);
    playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    var label = playing ? playBtn.dataset.labelPause : playBtn.dataset.labelPlay;
    if (label) playBtn.setAttribute('aria-label', label);
  }

  function syncMuteUI(card, video, muteBtn) {
    var unmuted = !video.muted;
    card.classList.toggle('is-unmuted', unmuted);
    muteBtn.setAttribute('aria-pressed', unmuted ? 'true' : 'false');
    var label = unmuted ? muteBtn.dataset.labelMute : muteBtn.dataset.labelUnmute;
    if (label) muteBtn.setAttribute('aria-label', label);
  }

  function toggleSound(card, video, allCards) {
    if (video.muted) {
      // One soundtrack at a time: mute every other card first
      allCards.forEach(function (other) {
        if (other === card) return;
        var otherVideo = other.querySelector('.ugc__video');
        if (otherVideo) otherVideo.muted = true;
      });
      video.muted = false;
      if (video.paused) {
        card.dataset.userPaused = 'false';
        playCard(card);
      }
    } else {
      video.muted = true;
    }
  }

  function setupCard(card, allCards) {
    var video = card.querySelector('.ugc__video');
    var playBtn = card.querySelector('[data-oji-v4-ugc-play]');
    var muteBtn = card.querySelector('[data-oji-v4-ugc-mute]');
    if (!video || !playBtn || !muteBtn) return;

    // Keep UI in sync no matter what triggered the change
    video.addEventListener('play', function () { syncPlayUI(card, video, playBtn); });
    video.addEventListener('pause', function () { syncPlayUI(card, video, playBtn); });
    video.addEventListener('volumechange', function () { syncMuteUI(card, video, muteBtn); });

    playBtn.addEventListener('click', function () {
      if (video.paused) {
        card.dataset.userPaused = 'false';
        playCard(card);
      } else {
        card.dataset.userPaused = 'true';
        pauseCard(card);
      }
    });

    muteBtn.addEventListener('click', function () { toggleSound(card, video, allCards); });

    // Tapping the video itself toggles sound (mirrors the mute button)
    video.addEventListener('click', function () { toggleSound(card, video, allCards); });
  }

  function initCards(section) {
    var cards = Array.prototype.slice.call(
      section.querySelectorAll('[data-oji-v4-ugc-card]')
    );
    if (!cards.length) return;

    cards.forEach(function (card) { setupCard(card, cards); });

    if (!('IntersectionObserver' in window)) return;

    var playObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var card = entry.target;
          if (entry.intersectionRatio >= VISIBLE_RATIO) {
            // Autoplay only in-view, never under reduced motion,
            // never over an explicit user pause
            if (!reducedMotion.matches && card.dataset.userPaused !== 'true') {
              playCard(card);
            }
          } else {
            pauseCard(card);
          }
        });
      },
      { threshold: [0, VISIBLE_RATIO] }
    );

    cards.forEach(function (card) { playObserver.observe(card); });
  }

  function initArrows(section) {
    var reel = section.querySelector('[data-oji-v4-ugc-reel]');
    var prevBtn = section.querySelector('[data-oji-v4-ugc-prev]');
    var nextBtn = section.querySelector('[data-oji-v4-ugc-next]');
    if (!reel || !prevBtn || !nextBtn) return;

    var stepSize = function () {
      var card = reel.querySelector('.ugc__card');
      if (!card) return reel.clientWidth * 0.8;
      var gap = parseFloat(window.getComputedStyle(reel).columnGap) || 16;
      return card.getBoundingClientRect().width + gap;
    };

    var scrollByStep = function (direction) {
      reel.scrollBy({
        left: direction * stepSize(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    var updateArrows = function () {
      var maxScroll = reel.scrollWidth - reel.clientWidth;
      prevBtn.disabled = reel.scrollLeft <= 1;
      nextBtn.disabled = reel.scrollLeft >= maxScroll - 1;
    };

    prevBtn.addEventListener('click', function () { scrollByStep(-1); });
    nextBtn.addEventListener('click', function () { scrollByStep(1); });

    var scrollTick = false;
    reel.addEventListener(
      'scroll',
      function () {
        if (scrollTick) return;
        scrollTick = true;
        window.requestAnimationFrame(function () {
          updateArrows();
          scrollTick = false;
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', updateArrows, { passive: true });

    updateArrows();
  }

  function initSection(section) {
    if (!section || section.dataset.ojiV4UgcReady === 'true') return;
    section.dataset.ojiV4UgcReady = 'true';

    initCards(section);
    initArrows(section);
  }

  function initAll() {
    document.querySelectorAll('.oji-v4 .ugc').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    var section = event.target.querySelector('.oji-v4 .ugc') || event.target.querySelector('.ugc');
    if (section) initSection(section);
  });
})();
